/** List all system logs with an option to search for
 * a specific user that performed a certain action
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled/macro';
import { Table, Space, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/react-hooks';
import { GET_LOGS } from 'services';
import { formatDate, formatTime, notify, ValidateUser } from 'utilities';
import { ListHeader, PrimaryTitle, SelectUser } from 'components';
import Filter from './filter';
import { useTranslation } from 'react-i18next';

const SearchContainer = styled.div`
  border-bottom: 1px solid #9b9b9b;

  .ant-form,
  .ant-form-inline {
    margin: 20px 0;
  }

  .ant-form label {
    font-size: 16px;
  }
`;
const LogsListContainer = styled.div``;

const LogsList = () => {
  ValidateUser();
  const [page, setPage] = useState(1);
  const [t, i18n] = useTranslation();
  const [pagination, setPagination] = useState({
    total: 5,
    pageSize: 5,
    showSizeChanger: false,
  });
  const [isSearching, setIsSearching] = useState(false);
  const searchInput = useRef(null);
  const [filteredValues, setFilteredValues] = useState({
    actorId: null,
    id: null,
    moduleId: null,
    moduleName: null,
    date: null,
  });

  const { data, loading, refetch } = useQuery(GET_LOGS, {
    variables: {
      first: 5,
      page: 1,
    },

    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const getPaginationObject = useCallback(() => {
    return {
      total: data?.listActivityLogs?.paginatorInfo?.total,
      pageSize: 5,
      showSizeChanger: true,
      position: 1,
    };
  });

  /*  useEffect(() => {
    setPagination(getPaginationObject());
    if (refetch && isSearching) {
      if (data?.listActivityLogs?.paginatorInfo?.total <= 5) {
        setPagination({});
      } else {
        setPagination(getPaginationObject());
      }
    }
  }, [data, isSearching, loading, refetch]);*/

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
    },
    {
      title: 'Date',
      render: (log) => formatDate(log?.created_at),
    },
    {
      title: 'Time',
      render: (log) => formatTime(log?.created_at),
    },
    {
      title: 'User',
      dataIndex: ['causer', 'name'],
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <SelectUser
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            forwardedRef={searchInput}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, 'causer')}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filteredValues.actorId,
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current.focus(), 100);
        }
      },
    },
    {
      title: 'User Type',
      dataIndex: 'causer_type',
    },
    {
      title: 'Module',
      dataIndex: 'entity',
    },
    {
      title: 'Module ID',
      dataIndex: 'subject_id',
    },
    {
      title: 'Module Properties',
      dataIndex: '',
      width: 100,
      render: (log) => {
        let data = JSON.parse(log.properties);
        return (
          <p
            style={{
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              width: '200px',
            }}
          >
            {data.attributes ? JSON.stringify(data.attributes) : ''}
          </p>
        );
      },
    },
    {
      title: 'Action Created',
      dataIndex: 'description',
    },
  ];
  //Translate Function For columns
  const ti81n = columns.map((ele) => {
    ele.title = t(ele.title);
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  // only called on user's manual page changed
  const onChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    if (filters['causer.name'] !== null) {
      refetch({
        page: pagination.current,
        actorId: filters['causer.name'],
        actorType: 'Admin',
      });
    } else {
      refetch({
        page: pagination.current,
        actorId: undefined,
      });
    }
    setFilteredValues({
      ...filteredValues,
      actorId: filters['causer.name'],
    });
  };

  return (
    <LogsListContainer>
      <ListHeader>
        <PrimaryTitle>{t('SystemLogContent')}</PrimaryTitle>
      </ListHeader>
      <SearchContainer>
        <Filter
          refetch={refetch}
          setIsSearching={() => {
            setIsSearching(true);
          }}
          resetPagination={() => {
            setPagination(getPaginationObject());
            setIsSearching(false);
          }}
          currentPage={page}
        />
      </SearchContainer>
      <Table
        scroll={{ x: 400 }}
        bordered
        dataSource={data?.listActivityLogs.data}
        columns={columns}
        loading={loading}
        rowKey="id"
        onChange={onChange}
        pagination={{
          total: data?.listActivityLogs?.paginatorInfo?.total + 1,
          pageSize: 5,
          showSizeChanger: false,
        }}
      />
    </LogsListContainer>
  );
};

export default LogsList;
